import { Box, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import {
  ChangeEventHandler,
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useState
} from 'react'
import { catchError, EMPTY, finalize, from, takeUntil } from 'rxjs'
import { PasswordApi } from 'src/api'
import { Button, Input, RequiredAsterisk, useAnalytic } from 'src/components'
import { ETrackingEvent } from 'src/enums'
import { useBehaviorMapper, useDebounceAnalyticInput, useUnsubscribe, useValidation } from 'src/hooks'
import { Logo } from 'src/images'
import { browserHistory, ERoutes, generate } from 'src/router'
import { SnackbarService } from 'src/services'
import { AuthModule } from 'src/store'
import { EventUtils, getApiErrorMessage } from 'src/utils'
import { resetPasswordSchema } from 'src/validation'
import { workEmailSchema } from 'src/validation/email'
import { ModalAuthentication } from '../modal-authentication'

interface IProps {
  noNavigation?: boolean
  onSwitch?: (stage: ComponentProps<typeof ModalAuthentication>['stage']) => void
  onSuccess?: () => void
}

export const ForgotPasswordForm: FC<IProps> = ({
  noNavigation,
  onSwitch,
  onSuccess
}) => {
  const unsubscribe$ = useUnsubscribe()
  const { eventHandler } = useAnalytic('pswd_reset1')
  const { analyticInput } = useDebounceAnalyticInput('pswd_reset1')
  const currentProfileEmail = useBehaviorMapper(AuthModule.profile$)?.email
  const [isSent, setIsSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  })
  useEffect(() => {
    if (currentProfileEmail) {
      setFormData((prev) => ({
        ...prev,
        email: currentProfileEmail
      }))
    }
  }, [currentProfileEmail])

  const { errors, reset, validate } = useValidation({
    data: formData,
    schema: resetPasswordSchema
  })

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(e => {
    e.persist()
    const { name, value } = e.target
    if (name === 'email') {
      analyticInput(ETrackingEvent.INPUT_EMAIL, value)
    }

    if (name === 'code') {
      analyticInput(ETrackingEvent.INPUT_PSWD_CODE, value)
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }, [analyticInput])

  const onGetCode = useCallback<Required<ComponentProps<typeof Button>>['onClick']>(async (e) => {
    EventUtils.preventDefault(e)
    const { isValid, value } = await validate({
      data: formData,
      schema: workEmailSchema
    })
    if (!isValid || !value) {
      return
    }

    setLoading(true)
    from(PasswordApi.forgotPasswordRequest(value))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error: AxiosError) => {
          SnackbarService.error(getApiErrorMessage(error))
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(() => {
        reset()
        setIsSent(true)
        setCountdown(60)
      })
  }, [formData, reset, unsubscribe$, validate])

  const onSubmit = useCallback<Required<ComponentProps<typeof Button>>['onClick']>(async (e) => {
    EventUtils.preventDefault(e)
    const { isValid, value } = await validate()
    if (!isValid || !value) {
      return
    }

    setLoading(true)
    const payload = {
      email: formData.email,
      code: formData.code,
      password: formData.newPassword
    }
    from(PasswordApi.resetPasswordRequest(payload))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error: AxiosError) => {
          SnackbarService.error(getApiErrorMessage(error))
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(({ data }) => {
        setIsSent(false)
        AuthModule.authenticated(data.profile)
        SnackbarService.success('Password reset successfully')
        if (onSuccess) {
          onSuccess()
        } else {
          browserHistory.push(generate(ERoutes.TALENT_DASHBOARD))
        }
      })
  }, [formData, onSuccess, unsubscribe$, validate])

  useEffect(() => {
    if (countdown > 0) {
      const timeout = setTimeout(() => {
        setCountdown((prev) => Math.max(prev - 1, 0))
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [countdown])

  return (
    <div
      className="fx-column gap-4"
      style={{ maxWidth: '464px', marginLeft: 'auto', marginRight: 'auto' }}
    >
      <div><img src={Logo} alt="logo"/></div>

      <Typography variant="h5" className="txt-neutral-theme-700">Reset your password</Typography>
      <div className="body-14 txt-black-02">
        {!isSent
          ? currentProfileEmail
            ? <>A verification code will be sent to <strong>{formData.email}</strong>.</>
            : <>Enter the email address associated with your account and we'll send you a code to reset your password.</>
          : <>A verification code has been sent to <strong>{formData.email}</strong>. Please check your inbox.</>}
      </div>

      {!isSent && !currentProfileEmail && (
        <Input
          label={<>Email<RequiredAsterisk/></>}
          id="email"
          type="email"
          name="email"
          placeholder="name@company.com"
          disabled={loading}
          readOnly={!!currentProfileEmail || isSent}
          value={currentProfileEmail || formData.email || ''}
          onChange={onChange}
          error={errors.getError('email')}
          required
        />
      )}

      {isSent && (
        <>
          <div className="fx-column gap-1">
            <Input
              label={<>Verification code<RequiredAsterisk/></>}
              id="code"
              type="text"
              name="code"
              placeholder="Enter the code sent to your email"
              value={formData.code || ''}
              onChange={onChange}
              error={errors.getError('code')}
              required
            />

            <div className="body-12 txt-black-02">
              Didn't receive the code?
              &nbsp;
              <strong>
                {countdown > 1
                  ? <span style={{ color: '#C0C0C0' }}>Resend ({countdown}s)</span>
                  : (
                    <span
                      className="pointer txt-color-blue-500"
                      onClick={eventHandler(ETrackingEvent.SIGN_UP_BTN_GET_CODE, onGetCode)}
                    >
                      Resend
                    </span>
                  )}
              </strong>
            </div>
          </div>
          <Input
            isCheckPassword
            label={<>New Password<RequiredAsterisk/></>}
            id="newPassword"
            type="password"
            name="newPassword"
            placeholder="Enter your password"
            value={formData.newPassword || ''}
            onChange={onChange}
            onClick={eventHandler(ETrackingEvent.INPUT_PSWD_NEW)}
            error={errors.getError('newPassword')}
            required
          />
          <Input
            label={<>Confirm Password<RequiredAsterisk/></>}
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            placeholder="Enter your password"
            value={formData.confirmPassword || ''}
            onChange={onChange}
            onClick={eventHandler(ETrackingEvent.INPUT_PASSWORD)}
            error={errors.getError('confirmPassword')}
            required
          />
        </>
      )}

      {!isSent
        ? (
          <Button
            className="round-16"
            disabled={loading}
            onClick={eventHandler(ETrackingEvent.BTN_RESET_PSWD_GET_CODE, onGetCode)}
          >
            Get Verification Code
          </Button>
        )
        : (
          <Button
            className="round-16"
            type="submit"
            disabled={loading || errors.hasError()}
            onClick={eventHandler(ETrackingEvent.BTN_CHANGE_PSWD, onSubmit)}
          >
            Change Password
          </Button>
        )}

      <Box display="flex" alignItems="center" justifyContent="space-between">
        {!noNavigation && (
          <Box>
            <Typography
              variant="body2-medium"
              className="txt-neutral-theme-400"
            >
              New to KNOWME?
            </Typography>
            {' '}
            <Typography
              variant="body2-medium"
              component="a"
              className="color-blue-500"
              onClick={eventHandler(ETrackingEvent.BTN_CREATE_ACCOUNT, () => onSwitch ? onSwitch('sign-up') : browserHistory.push(generate(ERoutes.SIGN_UP)))}
              sx={{ cursor: 'pointer' }}
            >
              Create account
            </Typography>
          </Box>
        )}
        {/* <Button
          className="round-16 px-20 ml-auto"
          type="submit"
          disabled={loading}
          onClick={eventHandler(ETrackingEvent.BTN_SEND_RESET_PSWD_LINK, onSubmit)}
        >
          Send
        </Button> */}
      </Box>
    </div>
  )
}
